import { useRouteContext } from '@/app/layouts/TenantLayout'
import { AccessControlled } from '@/components/access-control'
import { getUserPinnedProjectId, isPlatformUser } from '@/stores/auth'
import { useAuthStore } from '@/stores/authStore'
import { useCanCreateProject } from '@/utils/permissions'
import { SwitcherCreateRow, SwitcherPane } from './SwitcherPane'
import { usePinDefault, useProjectSwitch, useProjects } from './useSwitcherData'

interface ProjectPaneProps {
	/** The organization whose projects are listed. It need not be the one on screen. */
	orgId: string
	orgName: string
	onSwitch: () => void
	onCreateProjectClick: () => void
}

export function ProjectPane({ orgId, orgName, onSwitch, onCreateProjectClick }: ProjectPaneProps) {
	const { orgId: routeOrgId, tenantId } = useRouteContext()
	const projects = useProjects(orgId)
	const switchProject = useProjectSwitch()
	const pinnedId = useAuthStore(s => (isPlatformUser(s.user) ? getUserPinnedProjectId(s.user, orgId) : null))
	const { canPin, pin, isPinning } = usePinDefault('project')
	// The create dialog scopes to the org on screen, so another org's list offers no create row.
	const canCreate = useCanCreateProject(orgId) && orgId === routeOrgId

	return (
		<SwitcherPane
			testId='project-switcher'
			noun='project'
			heading={orgName}
			items={projects.map(project => ({
				id: project.id,
				name: project.name,
				keywords: [project.id],
				// Only the org on screen has a project on screen.
				selected: orgId === routeOrgId && project.id === tenantId,
				onSelect: () => {
					switchProject(project.id)
					onSwitch()
				},
			}))}
			pin={
				canPin
					? {
							pinnedId,
							disabled: isPinning,
							onToggle: (id, pinned) =>
								pin(current => {
									const { [orgId]: _, ...rest } = current.default_projects ?? {}
									return { ...current, default_projects: pinned ? rest : { ...rest, [orgId]: id } }
								}, pinned),
						}
					: undefined
			}
			footer={
				canCreate && (
					<AccessControlled resource='projects' action='create'>
						<SwitcherCreateRow testId='project-switcher-create' label='New project' onClick={onCreateProjectClick} />
					</AccessControlled>
				)
			}
		/>
	)
}
